/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
// FILE: src/components/AdminDashboard.js
// CRA-friendly Admin UI. Uses Hosting rewrites first, falls back to CF URL.
// Adds "Back to dashboard" button and performance tweaks:
// - Parallel fetch for detail + managers
// - Lazy-load AdminUsageWidget
// CHANGE: Added "Analytics" button in header (link to /admin/analytics)
// CHANGE (fix): Treat Suspended if auth.disabled OR profile.suspended === true
// CHANGE (UI): Show red "Un-suspend" button when suspended; otherwise "Suspend"
// CHANGE (robust): Clearer error handling around suspend/unsuspend

import React, { useEffect, useState, Suspense, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import SuperAdminNav from "./SuperAdminNav";
import { auth, db } from "../firebase";
import { sendPasswordResetEmail } from "firebase/auth";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import ManagerAccessCard from "./admin/ManagerAccessCard";
import { getPlanFromProfile, getStorageLimitBytes } from "../lib/planLimits";
import {
  PAGE_ACCESS,
  DEFAULT_MANAGER_MENU_KEYS,
  EXTRA_MANAGER_PAGES,
  buildNoneAccessMap,
  applyManagerDefaults,
  normalizePageAccess,
} from "../access/pageAccess";

// Lazy-load the heavy widget so it doesn't block first paint
const AdminUsageWidget = React.lazy(() => import("./admin/AdminUsageWidget"));

const DEFAULT_PAGE_SIZE = 25;
const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];
const VALID_ADMIN_TABS = new Set(["tenants", "users", "audit"]);
const ACTION_CODE_URL =
  process.env.REACT_APP_ACTION_CODE_URL ||
  (typeof window !== "undefined" ? window.location.origin : "");
const DEFAULT_REGION =
  process.env.REACT_APP_FUNCTION_REGION ||
  process.env.NEXT_PUBLIC_FUNCTION_REGION ||
  "us-central1";

// -------- Error Boundary --------
class AdminErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error: error };
  }
  componentDidCatch(error, info) {
    console.error("AdminErrorBoundary", error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="ad ad-wrap">
          <SuperAdminNav title="Admin" />
          <div className="ad-banner ad-banner--error">
            Admin UI crashed:{" "}
            {String(
              (this.state.error && this.state.error.message) ||
                this.state.error ||
                "Unknown error"
            )}
          </div>
          <AdStyles />
        </div>
      );
    }
    return this.props.children;
  }
}

// -------- Admin-only: open Stripe Billing Portal for a user --------
async function openBillingPortal(uid) {
  try {
    const u = auth.currentUser;
    if (!u) throw new Error("Not authenticated");
    const tok = await u.getIdToken();
    // Try Hosting rewrite first
    let r = await fetch("/api/createBillingPortalSession", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + tok,
      },
      body: JSON.stringify({ uid }),
    });
    if (!r.ok) {
      // Fallback to regional function
      const app = auth && auth.app;
      const projectId = (app && app.options && app.options.projectId) || "";
      const region =
        (typeof process !== "undefined" &&
          (process.env.REACT_APP_FUNCTION_REGION ||
            process.env.NEXT_PUBLIC_FUNCTION_REGION)) ||
        "us-central1";
      const base = "https://" + region + "-" + projectId + ".cloudfunctions.net";
      r = await fetch(base + "/createBillingPortalSession", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + tok,
        },
        body: JSON.stringify({ uid }),
      });
    }
    const j = await r.json();
    if (j && j.url) {
      window.open(j.url, "_blank", "noopener,noreferrer");
    } else {
      alert("Could not open billing portal.");
    }
  } catch (e) {
    alert(
      "Billing Portal error: " + (e && e.message ? e.message : String(e))
    );
  }
}

// -------- unified function caller (rewrites → fallback) --------
async function callAdminFunction(fnAndQuery, opts) {
  const options = opts || {};
  const u = auth.currentUser;
  if (!u) throw new Error("Not authenticated. Sign in with an admin account.");
  const idToken = await u.getIdToken(true);

  const split = String(fnAndQuery).split("?");
  const fnName = split[0];
  const queryStr = split.length > 1 ? split[1] : "";
  const q = queryStr ? `?${queryStr}` : "";

  const headers = new Headers(options.headers || {});
  headers.set("Authorization", `Bearer ${idToken}`);
  const method = (options.method || "GET").toUpperCase();
  const hasBody =
    typeof options.body !== "undefined" && options.body !== null;
  if (method !== "GET" && hasBody && !headers.has("Content-Type"))
    headers.set("Content-Type", "application/json");

  // Try hosting rewrite first (no CORS)
  const rewriteUrl = `/api/admin/${fnName}${q}`;
  try {
    const r = await fetch(rewriteUrl, {
      method,
      headers,
      mode: "same-origin",
      body: hasBody ? options.body : undefined,
      credentials: "same-origin",
    });
    const txt = await r.text();
    const ct = r.headers.get("content-type") || "";
    if (!r.ok) {
      let msg = `${r.status} ${r.statusText}`;
      if (ct.includes("application/json")) {
        try {
          const j = JSON.parse(txt);
          if (j && j.error) msg = j.error;
        } catch {}
      } else if (txt) {
        msg = txt.slice(0, 300);
      }
      throw new Error(msg);
    }
    return ct.includes("application/json")
      ? JSON.parse(txt)
      : JSON.parse(txt);
  } catch (e) {
    const app = auth && auth.app;
    const projectId =
      (app && app.options && app.options.projectId) ||
      process.env.REACT_APP_FIREBASE_PROJECT_ID ||
      process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID ||
      "";
    if (!projectId)
      throw new Error("Project ID not found (REACT_APP_FIREBASE_PROJECT_ID).");

    const url = `https://${DEFAULT_REGION}-${projectId}.cloudfunctions.net/${fnName}${q}`;
    const r2 = await fetch(url, {
      method,
      headers,
      mode: "cors",
      body: hasBody ? options.body : undefined,
      credentials: "same-origin",
    });
    const txt2 = await r2.text();
    const ct2 = r2.headers.get("content-type") || "";
    if (!r2.ok) {
      let msg = `${r2.status} ${r2.statusText}`;
      if (ct2.includes("application/json")) {
        try {
          const j = JSON.parse(txt2);
          if (j && j.error) msg = j.error;
        } catch {}
      } else if (txt2) {
        msg = txt2.slice(0, 300);
      }
      throw new Error(msg);
    }
    return ct2.includes("application/json")
      ? JSON.parse(txt2)
      : JSON.parse(txt2);
  }
}

// small utils
function fmtBytes(n) {
  var x = +n || 0,
    u = ["B", "KB", "MB", "GB", "TB"],
    i = 0;
  while (x >= 1024 && i < u.length - 1) {
    x /= 1024;
    i++;
  }



  return (
    x.toFixed(x >= 100 ? 0 : x >= 10 ? 1 : 2) + " " + u[i]
  );
}
function tsToLocal(ts) {
  if (!ts) return "—";
  const d = new Date(ts);
  return isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

function groupBy(arr, keyFn) {
  return (arr || []).reduce((acc, item) => {
    const k = keyFn(item);
    (acc[k] = acc[k] || []).push(item);
    return acc;
  }, {});
}
function Badge({ kind = "default", children }) {
  return (
    <span className={"ad-badge ad-badge--" + kind}>{children}</span>
  );
}

// Normalize your getUserOverview shape
function normalizeOverview(raw) {
  const o = (raw && raw.overview) ? raw.overview : raw || {};
  const authBlock = {
    uid: o.uid || (o.auth && o.auth.uid) || "",
    email: o.email || (o.auth && o.auth.email) || "",
    emailVerified:
      typeof o.emailVerified === "boolean"
        ? o.emailVerified
        : !!(o.auth && o.auth.emailVerified),
    disabled: !!(o.auth && o.auth.disabled),
    customClaims:
      o.customClaims || (o.auth && o.auth.customClaims) || {},
    metadata: {
      lastSignInTime:
        o.lastLogin ||
        (o.auth &&
          o.auth.metadata &&
          o.auth.metadata.lastSignInTime) ||
        null,
      creationTime:
        o.created ||
        (o.auth &&
          o.auth.metadata &&
          o.auth.metadata.creationTime) ||
        null,
    },
  };
  const profile = o.profile || {};
  const plan = o.plan || null;
  const usage = o.usage || { storageBytes: 0, objectCount: 0 };
  const counts = o.counts || {};
  return { auth: authBlock, profile, plan, usage, counts };
}

// WHY: derive suspended status from either Auth.disabled OR Firestore profile flag
function isSuspendedDetail(detail) {
  return !!(
    detail?.auth?.disabled || detail?.profile?.suspended === true
  );
}

function getAdminTabFromSearch(search) {
  const params = new URLSearchParams(search || "");
  const value = String(params.get("tab") || "tenants").trim().toLowerCase();
  return ["tenants", "users", "audit"].includes(value) ? value : "tenants";
}

function AdminDashboardInner() {
  const [isAdmin, setIsAdmin] = useState(false);
  const location = useLocation();
  const [err, setErr] = useState("");
// list
  const [tab, setTab] = useState(() => getAdminTabFromSearch(location.search));
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [tenantStatusFilter, setTenantStatusFilter] = useState("all");
  const [tenantBillingFilter, setTenantBillingFilter] = useState("all");
  const [loadingTenants, setLoadingTenants] = useState(false);
  const [tenantSeatDrafts, setTenantSeatDrafts] = useState({});
  const [tenantSeatBusyId, setTenantSeatBusyId] = useState("");
  const [tenantPlanDrafts, setTenantPlanDrafts] = useState({});
  const [tenantPlanBusyId, setTenantPlanBusyId] = useState("");
  const [pageToken, setPageToken] = useState("");
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [loadingUsers, setLoadingUsers] = useState(false);
  useEffect(() => {
    const nextTab = getAdminTabFromSearch(location.search);
    setTab((prev) => (prev === nextTab ? prev : nextTab));
  }, [location.search]);

  // NEW: filter users list by email (client-side for the current page)
  const [userEmailQuery, setUserEmailQuery] = useState("");
  const [userFromDate, setUserFromDate] = useState("");
  const [userToDate, setUserToDate] = useState("");
  const [userPlanFilter, setUserPlanFilter] = useState("");
  const [userVerifiedFilter, setUserVerifiedFilter] = useState("");
  // NEW: MFA flags for users on the current page (true/false/null/"unknown")
  const [mfaFlags, setMfaFlags] = useState({});
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditQuery, setAuditQuery] = useState("");
  const [auditTenantFilter, setAuditTenantFilter] = useState("");


  // detail
  const [view, setView] = useState("list");
  const [detail, setDetail] = useState(null);
  const [detailErr, setDetailErr] = useState("");
  const [detailLoading, setDetailLoading] = useState(false);

  // Account Managers (admin view)
  const [managers, setManagers] = useState([]);
  const [loadingManagers, setLoadingManagers] = useState(false);

  // Admin edit modal state for a manager
  const [mgrOpen, setMgrOpen] = useState(false);
  const [mgrEditingId, setMgrEditingId] = useState(null);
  const [mgrForm, setMgrForm] = useState({
    name: "",
    email: "",
    delivery: "upon_death",
    deliverAt: "",
    message: "",
    includeSecret: false,
    secretNote: "",
  });
  // Page-level permissions (admin view)
  // Account Managers are always read-only + permissioned via pageAccess
  const [mgrAccessMode, setMgrAccessMode] = useState("limited");
  const [mgrPageAccess, setMgrPageAccess] = useState({});
  const [mgrPermBusy, setMgrPermBusy] = useState(false);


  // Derived plan info for the *currently viewed* user
  const currentProfile =
    detail && detail.profile ? detail.profile : null;
  const derivedPlan = currentProfile
    ? getPlanFromProfile(currentProfile)
    : null;
  const derivedStorageLimit = currentProfile
    ? getStorageLimitBytes(currentProfile)
    : null;

  async function updateTenantSeatLimit(tenantId) {
    try {
      setTenantSeatBusyId(tenantId);

      const user = auth.currentUser;
      if (!user) throw new Error("Not signed in");

      const seatLimit = Number(tenantSeatDrafts[tenantId]);
      if (!Number.isFinite(seatLimit) || seatLimit < 1) {
        throw new Error("Seat limit must be at least 1");
      }

      const token = await user.getIdToken();

      const res = await fetch(
        "https://us-central1-lastwishbox-business.cloudfunctions.net/updateTenantSeatLimit",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ tenantId, seatLimit })
        }
      );

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || "Failed to update seat limit");

      setTenants((prev) =>
        prev.map((t) => {
          if (t.id !== tenantId) return t;
          const active = Number(t.activeMemberCount || 0);
          return {
            ...t,
            seatLimit,
            remainingSeats: Math.max(seatLimit - active, 0),
          };
        })
      );
    } catch (e) {
      alert(e.message || "Failed to update seat limit");
    } finally {
      setTenantSeatBusyId("");
    }
  }



  async function updateTenantPlan(tenantId) {
    try {
      setTenantPlanBusyId(tenantId);

      const user = auth.currentUser;
      if (!user) throw new Error("Not signed in");

      const planKey = String(tenantPlanDrafts[tenantId] || "custom");
      const token = await user.getIdToken();

      const res = await fetch(
        "/api/updateTenantPlan",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ tenantId, planKey })
        }
      );

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || "Failed to update tenant plan");

      setTenants((prev) =>
        prev.map((t) => {
          if (t.id !== tenantId) return t;
          const nextPlan = planKey;
          const nextSeatLimit =
            nextPlan === "starter" ? 25 :
            nextPlan === "growth" ? 50 :
            t.seatLimit;
          const active = Number(t.activeMemberCount || 0);
          return {
            ...t,
            planKey: nextPlan,
            seatLimit: nextSeatLimit,
            remainingSeats:
              nextSeatLimit == null || nextSeatLimit === ""
                ? null
                : Math.max(Number(nextSeatLimit) - active, 0),
          };
        })
      );

      if (planKey === "starter" || planKey === "growth") {
        setTenantSeatDrafts((prev) => ({
          ...prev,
          [tenantId]: String(planKey === "starter" ? 25 : 50)
        }));
      }
    } catch (e) {
      alert(e.message || "Failed to update tenant plan");
    } finally {
      setTenantPlanBusyId("");
    }
  }


  async function loadAuditLogs({ tenantId = "", q = "" } = {}) {
    try {
      setAuditLoading(true);

      const user = auth.currentUser;
      if (!user) throw new Error("Not signed in");

      const token = await user.getIdToken();

      const sp = new URLSearchParams();
      if (tenantId) sp.set("tenantId", tenantId);
      if (q) sp.set("q", q);

      const res = await fetch(`/api/getAdminAuditLogs?${sp.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || "Failed to load audit logs");

      setAuditLogs(Array.isArray(data.logs) ? data.logs : []);
    } catch (e) {
      setAuditLogs([]);
      alert(e.message || "Failed to load audit logs");
    } finally {
      setAuditLoading(false);
    }
  }

  useEffect(() => {
  const unsub = auth.onIdTokenChanged(async (u) => {
    if (!u) return setIsAdmin(false);

    try {
      const tok = await u.getIdTokenResult(true);

      let profile = {};
      try {
        const { getFirestore, doc, getDoc } = await import("firebase/firestore");
        const db = getFirestore();
        const snap = await getDoc(doc(db, "users", u.uid));
        profile = snap.exists() ? snap.data() : {};
      } catch {}

      setIsAdmin(
        !!(tok && tok.claims && tok.claims.admin) ||
        !!profile.isAdmin ||
        profile.role === "platform_admin" ||
        profile.tenantRole === "owner"
      );
    } catch {
      setIsAdmin(false);
    }
  });

  return () => {
    if (typeof unsub === "function") unsub();
  };
}, []);

  useEffect(() => {
    const nextTab = new URLSearchParams(location.search).get("tab") || "tenants";
    if (["tenants", "users", "audit"].includes(nextTab) && nextTab !== tab) {
      setTab(nextTab);
      setView("list");
      setErr("");
    }
  }, [location.search, tab]);

  useEffect(() => {
    if (!isAdmin) return;
    if (tab !== "users" || view !== "list") return;

    const q = String(userEmailQuery || "").trim();
    const delay = q ? 350 : 0;

    const t = setTimeout(() => {
      // Any search runs across *all* users server-side, so we reset pagination.
      setPageToken("");
      setNextPageToken(null);

      loadUsers({
        pageToken: "",
        pageSize: q ? pageSize : 200,
        q: q ? q : undefined,
      }).catch(() => {});
    }, delay);

    return () => clearTimeout(t);
  }, [isAdmin, tab, view, userEmailQuery, pageSize]);

  useEffect(() => {
    if (!isAdmin) return;
    if (tab !== "tenants") return;
    loadTenants().catch(() => {});
  }, [isAdmin, tab]);

  async function loadTenants() {
    setLoadingTenants(true);
    setErr("");
    try {
      const q = query(collection(db, "tenants"), orderBy("name"));
      const snap = await getDocs(q);
      const rows = await Promise.all(
        snap.docs.map(async (d) => {
          let memberCount = 0;
          let activeMemberCount = 0;

          try {
            const membersSnap = await getDocs(collection(db, "tenants", d.id, "members"));
            memberCount = membersSnap.size;
            activeMemberCount = membersSnap.docs.filter((m) => {
              const row = m.data() || {};
              return (row.status || "active") === "active";
            }).length;
          } catch {}

          const data = d.data() || {};
          const seatLimit =
            data.seatLimit == null || data.seatLimit === ""
              ? null
              : Number(data.seatLimit);

          const remainingSeats =
            seatLimit == null || !Number.isFinite(seatLimit)
              ? null
              : Math.max(seatLimit - activeMemberCount, 0);

          return {
            id: d.id,
            memberCount,
            activeMemberCount,
            remainingSeats,
            ...data
          };
        })
      );
      setTenants(rows);
      setTenantSeatDrafts(
        Object.fromEntries(
          rows.map((r) => [r.id, r.seatLimit == null || r.seatLimit === "" ? "" : String(r.seatLimit)])
        )
      );
      setTenantPlanDrafts(
        Object.fromEntries(
          rows.map((r) => [r.id, r.planKey || "custom"])
        )
      );
    } catch (e) {
      setTenants([]);
      setErr("Tenants error: " + (e.message || String(e)));
    } finally {
      setLoadingTenants(false);
    }
  }

  // NEW: Pull MFA status for users in the list view.
  // Uses the existing getUserOverview admin endpoint so we don't need extra Firestore rules.
  async function fetchMfaForList(list) {
    try {
      if (!Array.isArray(list) || list.length === 0) return;
      const uids = list.map((u) => u && u.uid).filter(Boolean);

      // Prime MFA flags from listUsers (server attaches Firestore profile flags)
      const pre = {};
      list.forEach((u) => {
        if (u && u.uid && typeof u.mfaEnabled === "boolean") pre[u.uid] = u.mfaEnabled;
      });
      if (Object.keys(pre).length) {
        setMfaFlags((prev) => ({ ...prev, ...pre }));
      }

      // Determine which uids we haven't fetched yet
      const missing = uids.filter(
        (uid) =>
          !Object.prototype.hasOwnProperty.call(mfaFlags, uid) &&
          !Object.prototype.hasOwnProperty.call(pre, uid)
      );
      if (!missing.length) return;

      // Mark as loading (null)
      setMfaFlags((prev) => {
        const next = { ...prev };
        missing.forEach((uid) => {
          if (!Object.prototype.hasOwnProperty.call(next, uid)) next[uid] = null;
        });
        return next;
      });

      const CONCURRENCY = 6;
      for (let i = 0; i < missing.length; i += CONCURRENCY) {
        const chunk = missing.slice(i, i + CONCURRENCY);
        const settled = await Promise.allSettled(
          chunk.map((uid) =>
            callAdminFunction(
              "getUserOverview?uid=" + encodeURIComponent(uid),
              { method: "GET" }
            )
          )
        );

        const patch = {};
        settled.forEach((res, idx) => {
          const uid = chunk[idx];
          if (res.status === "fulfilled") {
            const ov = normalizeOverview(res.value);
            patch[uid] = !!(ov && ov.profile && ov.profile.mfa && ov.profile.mfa.enabled);
          } else {
            patch[uid] = "unknown";
          }
        });

        setMfaFlags((prev) => ({ ...prev, ...patch }));
      }
    } catch (e) {
      // silent: list view should still render
      console.warn("fetchMfaForList error", e);
    }
  }


  async function loadUsers(opts) {
    const options = opts || {};
    setLoadingUsers(true);
    setErr("");
    try {
      const pt =
        typeof options.pageToken !== "undefined"
          ? options.pageToken
          : pageToken;
      const qs = new URLSearchParams();
      const limit =
        typeof options.pageSize !== "undefined" ? options.pageSize : pageSize;
      qs.set("limit", String(limit || DEFAULT_PAGE_SIZE));

      const q =
        typeof options.q !== "undefined" ? String(options.q || "").trim() : "";
      if (q) {
        qs.set("q", q);
      } else if (pt) {
        qs.set("pageToken", pt);
      }
      const data = await callAdminFunction(
        "listUsers?" + qs.toString(),
        { method: "GET" }
      );
            const list = Array.isArray(data.users) ? data.users : [];
      setUsers(list);
      // NEW: fetch MFA status for users shown on this page
      fetchMfaForList(list).catch(() => {});
      setNextPageToken(data.nextPageToken || null);
      setPageToken(pt || "");
    } catch (e) {
      setUsers([]);
      setErr("Users error: " + (e.message || String(e)));
    } finally {
      setLoadingUsers(false);
    }
  }

  function openManage(u) {
    setView("detail");
    loadDetail(u.uid).catch(() => {});
  }

  // PERF: load overview + managers in parallel and show skeleton while loading
  async function loadDetail(userUid) {
    setDetail(null);
    setDetailErr("");
    setDetailLoading(true);
    setManagers([]);
    setLoadingManagers(true);
    try {
      const enc = encodeURIComponent(userUid);
      const u = auth.currentUser;
      const tok = await u.getIdToken();

      const overviewPromise = callAdminFunction(
        "getUserOverview?uid=" + enc,
        { method: "GET" }
      ).then((data) => normalizeOverview(data));

      const managersPromise = fetch(
        `/api/accountManagers/adminListForUser?uid=${enc}`,
        {
          headers: { Authorization: `Bearer ${tok}` },
        }
      ).then(async (r) => {
        const j = await r.json();
        if (!r.ok) throw new Error(j.error || "Failed to load account managers");
        return Array.isArray(j.items) ? j.items : [];
      });

      const [norm, mgrs] = await Promise.allSettled([
        overviewPromise,
        managersPromise,
      ]);

      if (norm.status === "fulfilled") {
        setDetail(norm.value);
      } else {
        setDetailErr(
          norm.reason?.message || "Failed to load user overview."
        );
      }

      if (mgrs.status === "fulfilled") {
        setManagers(mgrs.value);
      } else {
        console.error("load managers", mgrs.reason);
      }
    } finally {
      setDetailLoading(false);
      setLoadingManagers(false);
    }
  }

  async function postAdmin(fn, body) {
    return callAdminFunction(fn, {
      method: "POST",
      body: JSON.stringify(body || {}),
    });
  }

  async function setAdminFlag(userUid, makeAdmin) {
    try {
      await postAdmin("setAdmin", {
        uid: userUid,
        admin: !!makeAdmin,
      });
      await loadDetail(userUid);
    } catch (e) {
      alert(e.message || "Failed to set admin");
    }
  }

  async function suspend(userUid) {
    try {
      const r = await postAdmin("suspendUser", { uid: userUid });
      if (!r || r.error) throw new Error(r?.error || "suspend failed");
      await loadDetail(userUid); // refresh view to reflect disabled/profile.suspended
    } catch (e) {
      alert(e.message || "Failed to suspend");
    }
  }

  async function unsuspend(userUid) {
    try {
      const r = await postAdmin("unsuspendUser", { uid: userUid });
      if (!r || r.error) throw new Error(r?.error || "unsuspend failed");
      await loadDetail(userUid); // refresh
    } catch (e) {
      alert(e.message || "Failed to unsuspend");
    }
  }

  // ---- Sends ----
  async function sendReset(email) {
    if (!email) return alert("No email on file for this user.");
    try {
      // Prefer SendGrid function so it shows in SendGrid Activity
      const u = auth.currentUser;
      const tok = await u.getIdToken();
      // Try your main endpoint first; if your project used a different name earlier, we fallback.
      const body = { email, url: `${ACTION_CODE_URL}/reset-password` };
      let r = await fetch("/api/auth/sendReset", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${tok}`,
        },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        // Fallback to legacy/alt endpoint if you have it
        r = await fetch("/api/auth/sendResetFor", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${tok}`,
          },
          body: JSON.stringify(body),
        });
      }
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.error || `HTTP ${r.status}`);
      }
      alert("Password reset sent to " + email);
    } catch (e1) {
      // Fallback to Firebase default
      try {
        await sendPasswordResetEmail(auth, email, {
          url: `${ACTION_CODE_URL}/reset-password`,
          handleCodeInApp: false,
        });
        alert("Password reset (fallback) sent to " + email);
      } catch (e2) {
        alert(
          (e2 && e2.message) ||
            (e1 && e1.message) ||
            "Failed to send reset"
        );
      }
    }
  }

  async function sendVerify(userUid) {
    try {
      const res = await postAdmin("sendVerifyEmail", {
        uid: userUid,
        url: ACTION_CODE_URL,
      });
      if (res && (res.sent || res.ok === true)) {
        alert("Verification email sent");
      } else {
        throw new Error(
          (res && (res.error || res.message)) ||
            "Unknown response from backend"
        );
      }
    } catch (e) {
      alert(
        e.message ||
          "Failed to send verification (ensure the admin Cloud Function is deployed & reachable)"
      );
    }
  }

  async function markVerified(userUid, v) {
    try {
      await postAdmin("setEmailVerified", {
        uid: userUid,
        verified: !!v,
      });
      await loadDetail(userUid);
    } catch (e) {
      alert(e.message || "Failed to update verification");
    }
  }

  async function removeUser(userUid) {
    if (
      !window.confirm(
        "Delete user account (Auth + profile)? This cannot be undone."
      )
    )
      return;
    try {
      await postAdmin("deleteUser", { uid: userUid });
      setView("list");
      await loadUsers({ pageToken: "", pageSize });
    } catch (e) {
      alert(e.message || "Failed to delete user");
    }
  }

  async function savePlan(userUid) {
    const plan =
      (document.getElementById("ad-planId") || {}).value || "";
    const maxStorageBytes =
      +(document.getElementById("ad-maxStorageBytes") || {})
        .value || null;
    const maxItems =
      +(document.getElementById("ad-maxItems") || {}).value ||
      null;
    try {
      await postAdmin("setUserPlan", {
        uid: userUid,
        plan,
        limits: { maxStorageBytes, maxItems },
      });
      await loadDetail(userUid);
    } catch (e) {
      alert(e.message || "Failed to save plan");
    }
  }

  // ---- Admin edit manager modal helpers ----
  function openMgr(item) {
    if (!detail?.auth?.uid) return;
    setMgrEditingId(item?.id || null);
    setMgrForm({
      name: item?.name || item?.recipient || "",
      email: item?.email || "",
      delivery: item?.delivery || "upon_death",
      deliverAt: item?.deliverAt
        ? item.deliverAt.seconds
          ? new Date(item.deliverAt.seconds * 1000)
          : new Date(item.deliverAt)
        : null,
      message: item?.message || "",
      includeSecret: !!item?.includeSecret,
      secretNote: item?.secretNote || "",
    });
    // Page permissions (managers are always limited)
    setMgrAccessMode("limited");
    const existing = item && typeof item.pageAccess === "object" ? normalizePageAccess(item.pageAccess) : buildNoneAccessMap();
    setMgrPageAccess(applyManagerDefaults(existing));

    setMgrOpen(true);
  }
  function closeMgr() {
    setMgrOpen(false);
    setMgrEditingId(null);
    setMgrAccessMode("limited");
    setMgrPageAccess(applyManagerDefaults(buildNoneAccessMap()));
    setMgrPermBusy(false);

  }
  function setMgrField(k, v) {
    setMgrForm((f) => ({ ...f, [k]: v }));
  }

  async function adminSaveManager() {
    if (!detail?.auth?.uid) return;
    try {
      const u = auth.currentUser;
      const tok = await u.getIdToken();
      const payload = {
        uid: detail.auth.uid,
        id: mgrEditingId || undefined,
        name: mgrForm.name,
        email: mgrForm.email,
        delivery: mgrForm.delivery,
        deliverAt:
          mgrForm.delivery === "scheduled" && mgrForm.deliverAt
            ? new Date(mgrForm.deliverAt).toISOString()
            : null,
        message: mgrForm.message,
        includeSecret: !!mgrForm.includeSecret,
        secretNote: mgrForm.includeSecret ? mgrForm.secretNote : "",
      };
      const r = await fetch("/api/accountManagers/adminUpsert", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${tok}`,
        },
        body: JSON.stringify(payload),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Save failed");

      // reload list
      await loadDetail(detail.auth.uid);
      closeMgr();
    } catch (e) {
      alert(e.message || "Save failed");
    }
  }

  async function adminDeleteManager() {
    if (!detail?.auth?.uid || !mgrEditingId) return;
    if (!window.confirm("Delete this account manager?")) return;
    try {
      const u = auth.currentUser;
      const tok = await u.getIdToken();
      const r = await fetch("/api/accountManagers/adminDelete", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${tok}`,
        },
        body: JSON.stringify({ id: mgrEditingId }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Delete failed");
      await loadDetail(detail.auth.uid);
      closeMgr();
    } catch (e) {
      alert(e.message || "Delete failed");
    }
  }

  async function adminResendManager(uid, id) {
    try {
      const u = auth.currentUser;
      const tok = await u.getIdToken();
      const r = await fetch("/api/accountManagers/adminSend", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${tok}`,
        },
        body: JSON.stringify({ uid, id }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Failed to send");
      alert("Email sent");
      await loadDetail(uid);
    } catch (e) {
      alert(e.message || "Failed to send");
    }
  }

  function adminTogglePage(key, on) {
    setMgrPageAccess((prev) => applyManagerDefaults({ ...(prev || {}), [key]: !!on }));
  }

  async function adminSaveManagerPermissions() {
    if (!detail?.auth?.uid || !mgrEditingId) {
      alert("Open an existing manager (Edit) to set permissions.");
      return;
    }
    try {
      setMgrPermBusy(true);
      const u = auth.currentUser;
      const tok = await u.getIdToken();
      const payload = {
        uid: detail.auth.uid,
        id: mgrEditingId,
        accessMode: "limited",
        pageAccess: applyManagerDefaults(normalizePageAccess(mgrPageAccess || {})),
      };
      const r = await fetch("/api/accountManagers/adminSetPermissions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${tok}`,
        },
        body: JSON.stringify(payload),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || "Failed to save permissions");
      if (!j || j.ok !== true) throw new Error("Failed to save permissions (bad response)");
      await loadDetail(detail.auth.uid);
      alert("Permissions saved");
    } catch (e) {
      alert(e.message || "Failed to save permissions");
    } finally {
      setMgrPermBusy(false);
    }
  }


  const filteredTenants = useMemo(() => {
    return (tenants || []).filter((tenant) => {
      const status = String(tenant?.status || "").toLowerCase();
      const billingStatus = String(tenant?.billingStatus || "").toLowerCase();

      const statusOk =
        tenantStatusFilter === "all" ||
        status === tenantStatusFilter;

      const billingOk =
        tenantBillingFilter === "all" ||
        billingStatus === tenantBillingFilter;

      return statusOk && billingOk;
    });
  }, [tenants, tenantStatusFilter, tenantBillingFilter]);

  if (!isAdmin) {
    return (
      <div className="ad ad-wrap">
              <SuperAdminNav title="Admin" active={tab} />
        <div className="ad-banner ad-banner--error">
          Admin access only. Your account lacks the <code>admin</code> claim.
        </div>
        <AdStyles />
      </div>
    );
  }

  // WHY: For the list table we also try to reflect suspension if backend includes a Firestore flag
  function rowSuspended(u) {
    return !!(u.disabled || u.suspended === true);
  }

  const emailQuery = String(userEmailQuery || "").trim().toLowerCase();
  const visibleUsers = users.filter((u) => {
    if (emailQuery && !String((u && u.email) || "").toLowerCase().includes(emailQuery)) return false;
    if (userFromDate) {
      const created = u.metadata?.creationTime ? new Date(u.metadata.creationTime).getTime() : 0;
      const fromMs = new Date(userFromDate + "T00:00:00").getTime();
      if (created < fromMs) return false;
    }
    if (userToDate) {
      const created = u.metadata?.creationTime ? new Date(u.metadata.creationTime).getTime() : 0;
      const toMs = new Date(userToDate + "T23:59:59.999").getTime();
      if (created > toMs) return false;
    }
    if (userPlanFilter) {
      const plan = String(u.planKey || u.subscriptionStatus || "").toLowerCase();
      if (userPlanFilter === "free" && plan !== "free") return false;
      if (userPlanFilter !== "free" && plan !== userPlanFilter) return false;
    }
    if (userVerifiedFilter === "verified" && !u.emailVerified) return false;
    if (userVerifiedFilter === "unverified" && u.emailVerified) return false;
    return true;
  });


  function exportUsersCSV(userList, isSuspended) {
    function esc(v) { const s = String(v == null ? "" : v); return s.includes(",") || s.includes('"') || s.includes("\n") ? '"' + s.replace(/"/g, '""') + '"' : s; }
    const headers = ["Email","Plan","Verified","Status","Created","Last login"];
    const lines = [headers.map(esc).join(",")];
    userList.forEach(u => {
      lines.push([
        u.email || "",
        u.planKey || u.subscriptionStatus || "—",
        u.emailVerified ? "Yes" : "No",
        isSuspended(u) ? "Suspended" : "Active",
        u.metadata?.creationTime || "",
        u.metadata?.lastSignInTime || "",
      ].map(esc).join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "users-" + new Date().toISOString().slice(0,10) + ".csv";
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="ad ad-wrap">
      <SuperAdminNav title="Admin" active={tab} />

      {err ? (
        <div className="ad-banner ad-banner--error">{err}</div>
      ) : null}



      {/* TENANTS */}
      {tab === "tenants" ? (
        <section>
          <div className="ad-card">
            <h2 style={{ marginTop: 0 }}>Tenants</h2>

            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: "#657899" }}>Tenant Status</label>
                <select
                  value={tenantStatusFilter}
                  onChange={(e) => setTenantStatusFilter(e.target.value)}
                  style={{
                    minWidth: 180,
                    height: 40,
                    borderRadius: 10,
                    border: "1px solid #d7dce8",
                    padding: "0 12px",
                    fontSize: 14,
                    background: "#fff",
                    color: "#2a0516",
                  }}
                >
                  <option value="all">All statuses</option>
                  <option value="active">Active</option>
                  <option value="pending_payment">Pending payment</option>
                  <option value="inactive">Inactive</option>
                  <option value="payment_issue">Payment issue</option>
                </select>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: "#657899" }}>Billing Status</label>
                <select
                  value={tenantBillingFilter}
                  onChange={(e) => setTenantBillingFilter(e.target.value)}
                  style={{
                    minWidth: 180,
                    height: 40,
                    borderRadius: 10,
                    border: "1px solid #d7dce8",
                    padding: "0 12px",
                    fontSize: 14,
                    background: "#fff",
                    color: "#2a0516",
                  }}
                >
                  <option value="all">All billing</option>
                  <option value="active">Active</option>
                  <option value="pending_checkout">Pending checkout</option>
                  <option value="pending">Pending</option>
                  <option value="inactive">Inactive</option>
                  <option value="overdue">Overdue</option>
                </select>
              </div>
            </div>

            {loadingTenants ? (
              <div className="ad-loading">Loading tenants…</div>
            ) : tenants.length === 0 ? (
              <div className="ad-empty">No tenants found.</div>
            ) : (
              <div className="ad-table-wrap">
                <table className="ad-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Status</th>
                      <th>Billing</th>
                      <th>Plan</th>
                      <th>Members</th>
                      <th>Seat Limit</th>
                      <th>Remaining Seats</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTenants.map((t) => (
                      <tr key={t.id}>
                        <td data-label="Name">{t.name || "—"}</td>
                        <td data-label="Status">
                          {String(t.status || "").toLowerCase() === "active" ? (
                            <Badge kind="ok">Active</Badge>
                          ) : String(t.status || "").toLowerCase() === "pending_payment" ? (
                            <Badge>Pending payment</Badge>
                          ) : String(t.status || "").toLowerCase() === "payment_issue" ? (
                            <Badge>Payment issue</Badge>
                          ) : String(t.status || "").toLowerCase() === "inactive" ? (
                            <Badge>Inactive</Badge>
                          ) : (
                            <Badge>{t.status || "—"}</Badge>
                          )}
                        </td>
                        <td data-label="Billing">
                          {String(t.billingStatus || "").toLowerCase() === "active" ? (
                            <Badge kind="ok">Active</Badge>
                          ) : String(t.billingStatus || "").toLowerCase() === "pending_checkout" ? (
                            <Badge>Pending checkout</Badge>
                          ) : String(t.billingStatus || "").toLowerCase() === "pending" ? (
                            <Badge>Pending</Badge>
                          ) : String(t.billingStatus || "").toLowerCase() === "overdue" ? (
                            <Badge>Overdue</Badge>
                          ) : String(t.billingStatus || "").toLowerCase() === "inactive" ? (
                            <Badge>Inactive</Badge>
                          ) : (
                            <Badge>{t.billingStatus || "—"}</Badge>
                          )}
                        </td>
                        <td data-label="Plan">
                          <div style={{ display: "inline-flex", gap: 8, alignItems: "center", flexWrap: "nowrap", whiteSpace: "nowrap" }}>
                            <select
                              value={tenantPlanDrafts[t.id] || t.planKey || "custom"}
                              onChange={(e) =>
                                setTenantPlanDrafts((prev) => ({
                                  ...prev,
                                  [t.id]: e.target.value
                                }))
                              }
                              style={{
                                height: 34,
                                borderRadius: 6,
                                border: "1px solid #d6dbe7",
                                padding: "0 10px",
                                minWidth: 108,
                                background: "#fff"
                              }}
                            >
                              <option value="starter">starter</option>
                              <option value="growth">growth</option>
                              <option value="custom">custom</option>
                            </select>

                            <button
                              type="button"
                              onClick={() => updateTenantPlan(t.id)}
                              disabled={
                                tenantPlanBusyId === t.id ||
                                String(tenantPlanDrafts[t.id] || "custom") === String(t.planKey || "custom")
                              }
                              className="ad-btn"
                            >
                              {tenantPlanBusyId === t.id ? "Saving..." : "Save"}
                            </button>
                          </div>
                        </td>
                        <td data-label="Members">
                          {t.activeMemberCount ?? 0}
                          <span style={{ color: "#657899" }}> / {t.memberCount ?? 0}</span>
                        </td>
                        <td data-label="Seat Limit">
                          <div style={{ display: "inline-flex", gap: 8, alignItems: "center", flexWrap: "nowrap", whiteSpace: "nowrap" }}>
                            <input
                              type="number"
                              min="1"
                              value={tenantSeatDrafts[t.id] ?? ""}
                              onChange={(e) =>
                                setTenantSeatDrafts((prev) => ({
                                  ...prev,
                                  [t.id]: e.target.value
                                }))
                              }
                              style={{
                                width: 72,
                                height: 34,
                                borderRadius: 6,
                                border: "1px solid #d6dbe7",
                                padding: "0 8px"
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => updateTenantSeatLimit(t.id)}
                              disabled={
                                tenantPlanDrafts[t.id] !== "custom" ||
                                tenantSeatBusyId === t.id ||
                                String(tenantSeatDrafts[t.id] ?? "") === String(t.seatLimit ?? "")
                              }
                              className="ad-btn"
                            >
                              {tenantSeatBusyId === t.id ? "Saving..." : "Save"}
                            </button>
                          </div>
                        </td>
                        <td data-label="Remaining Seats">
                          {t.remainingSeats == null ? "—" : t.remainingSeats}
                        </td>
                        <td data-label="Actions">
                          <Link className="ad-btn ad-btn--primary" to={`/admin/tenant/${t.id}`}>
                            Open
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      ) : null}


      {/* AUDIT */}
      {tab === "audit" ? (
        <section>
          <div className="ad-card">
            <h2 style={{ marginTop: 0 }}>Audit Log</h2>

            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
              <input
                type="text"
                value={auditQuery}
                onChange={(e) => setAuditQuery(e.target.value)}
                placeholder="Search by action, actor, target, tenant"
                style={{
                  minWidth: 280,
                  height: 40,
                  borderRadius: 10,
                  border: "1px solid #d6dbe7",
                  padding: "0 12px",
                  fontSize: 14
                }}
              />

              <select
                value={auditTenantFilter}
                onChange={(e) => setAuditTenantFilter(e.target.value)}
                style={{
                  minWidth: 220,
                  height: 40,
                  borderRadius: 10,
                  border: "1px solid #d6dbe7",
                  padding: "0 12px",
                  fontSize: 14,
                  background: "#fff"
                }}
              >
                <option value="">All tenants</option>
                {tenants.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name || t.id}
                  </option>
                ))}
              </select>

              <button
                type="button"
                className="ad-btn ad-btn--primary"
                onClick={() =>
                  loadAuditLogs({
                    tenantId: auditTenantFilter,
                    q: auditQuery
                  })
                }
              >
                Search
              </button>
            </div>

            {auditLoading ? (
              <div className="ad-loading">Loading audit logs…</div>
            ) : auditLogs.length === 0 ? (
              <div className="ad-empty">No audit logs found.</div>
            ) : (
              <div className="ad-table-wrap">
                <table className="ad-table">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Action</th>
                      <th>Tenant</th>
                      <th>Actor</th>
                      <th>Target</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLogs.map((log) => (
                      <tr key={log.id}>
                        <td data-label="When">
                          {log.createdAtMs ? new Date(log.createdAtMs).toLocaleString() : "—"}
                        </td>
                        <td data-label="Action">{log.action || "—"}</td>
                        <td data-label="Tenant">{log.tenantId || "—"}</td>
                        <td data-label="Actor">{log.actorEmail || log.actorUid || "—"}</td>
                        <td data-label="Target">{log.targetEmail || log.targetUid || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      ) : null}

{/* USERS LIST */}
      {tab === "users" && view === "list" ? (
        <section>
          <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"flex-end", marginBottom:12, padding:12, background:"#f9fafb", borderRadius:8, border:"1px solid #e5e7eb" }}>
            <div style={{ display:"flex", flexDirection:"column", gap:2 }}>
              <label style={{ fontSize:11, color:"#6b7280", fontWeight:600 }}>From</label>
              <input type="date" value={userFromDate} onChange={e => setUserFromDate(e.target.value)} className="ad-input" style={{ padding:"6px 8px", fontSize:13 }} />
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:2 }}>
              <label style={{ fontSize:11, color:"#6b7280", fontWeight:600 }}>To</label>
              <input type="date" value={userToDate} onChange={e => setUserToDate(e.target.value)} className="ad-input" style={{ padding:"6px 8px", fontSize:13 }} />
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:2 }}>
              <label style={{ fontSize:11, color:"#6b7280", fontWeight:600 }}>Plan</label>
              <select value={userPlanFilter} onChange={e => setUserPlanFilter(e.target.value)} className="ad-input" style={{ padding:"6px 8px", fontSize:13 }}>
                <option value="">All plans</option>
                <option value="free">Free</option>
                <option value="basic">Basic</option>
                <option value="standard">Standard</option>
                <option value="advanced">Advanced</option>
                <option value="family">Family</option>
              </select>
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:2 }}>
              <label style={{ fontSize:11, color:"#6b7280", fontWeight:600 }}>Verified</label>
              <select value={userVerifiedFilter} onChange={e => setUserVerifiedFilter(e.target.value)} className="ad-input" style={{ padding:"6px 8px", fontSize:13 }}>
                <option value="">All</option>
                <option value="verified">Verified</option>
                <option value="unverified">Unverified</option>
              </select>
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:2, flex:1, minWidth:160 }}>
              <label style={{ fontSize:11, color:"#6b7280", fontWeight:600 }}>Search email</label>
              <input className="ad-input" value={userEmailQuery} onChange={e => setUserEmailQuery(e.target.value)} placeholder="Search by email…" style={{ padding:"6px 8px", fontSize:13 }} />
            </div>
            <button className="ad-btn" type="button" onClick={() => { setUserEmailQuery(""); setUserFromDate(""); setUserToDate(""); setUserPlanFilter(""); setUserVerifiedFilter(""); }}>
              Clear filters
            </button>
            <button className="ad-btn" type="button" onClick={() => exportUsersCSV(visibleUsers, rowSuspended)} disabled={visibleUsers.length === 0}>
              Export CSV
            </button>
            <span style={{ fontSize:13, color:"#6b7280", paddingBottom:2 }}>
              Total: <b>{users.length}</b> · Showing: <b>{visibleUsers.length}</b>
            </span>
          </div>
          <div className="ad-table-wrap">
            <table className="ad-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Plan</th>
                  <th>Status</th>
                  <th>Verified</th>
                  <th>MFA</th>
                  <th>Created</th>
                  <th>Last login</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {loadingUsers ? (
                  <tr>
                    <td colSpan={8} className="ad-loading">
                      Loading…
                    </td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="ad-empty">
                      No users found.
                    </td>
                  </tr>
                ) : visibleUsers.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="ad-empty">
                      No users match your filters.
                    </td>
                  </tr>
                ) : (
                  visibleUsers.map((u) => (
                    <tr key={u.uid}>
                      <td data-label="Email" className="ad-break">
                        {u.email || <em>—</em>}
                      </td>
                      <td data-label="Plan">
                        <span style={{
                          padding:"2px 8px", borderRadius:999, fontSize:12, fontWeight:600,
                          background: u.planKey==="free"||u.subscriptionStatus==="free" ? "#f0f9ff" : u.subscriptionStatus==="active" ? "#ecfdf5" : "#f9fafb",
                          color: u.planKey==="free"||u.subscriptionStatus==="free" ? "#0369a1" : u.subscriptionStatus==="active" ? "#065f46" : "#374151",
                          border:"1px solid #e5e7eb"
                        }}>
                          {u.planKey || u.subscriptionStatus || "—"}
                        </span>
                      </td>
                      <td data-label="Status">
                        {rowSuspended(u) ? (
                          <Badge kind="danger">Suspended</Badge>
                        ) : (
                          <Badge kind="ok">Active</Badge>
                        )}
                      </td>
                      <td data-label="Verified">
                        {u.emailVerified ? (
                          <Badge kind="ok">Yes</Badge>
                        ) : (
                          <Badge>No</Badge>
                        )}
                      </td>
                      <td data-label="MFA">
                        {mfaFlags[u.uid] === true ? (
                          <Badge kind="ok">Enabled</Badge>
                        ) : mfaFlags[u.uid] === false ? (
                          <Badge>Not enabled</Badge>
                        ) : mfaFlags[u.uid] === "unknown" ? (
                          <Badge>Unknown</Badge>
                        ) : (
                          <Badge>…</Badge>
                        )}
                      </td>
                      <td data-label="Created">
                        {tsToLocal(u.metadata && u.metadata.creationTime)}
                      </td>
                      <td data-label="Last login">
                        {tsToLocal(u.metadata && u.metadata.lastSignInTime)}
                      </td>
                      <td className="ad-actions">
                        <button
                          className="ad-btn ad-btn--primary"
                          onClick={() => openManage(u)}
                        >
                          Manage
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="ad-pager">
            <button
              className="ad-btn"
              disabled={!!emailQuery || !pageToken}
              onClick={() => {
                setPageToken("");
                loadUsers({ pageToken: "", pageSize });
              }}
            >
              First page
            </button>
            <button
              className="ad-btn"
              disabled={!!emailQuery || !nextPageToken}
              onClick={() => {
                setPageToken(nextPageToken);
                loadUsers({ pageToken: nextPageToken, pageSize });
              }}
            >
              Next page
            </button>
          </div>
        </section>
      ) : null}

      {/* USER DETAIL */}
      {tab === "users" && view === "detail" ? (
        <section className="ad-detail">
          <div className="ad-detail-header">
            <button
              className="ad-btn"
              onClick={() => setView("list")}
            >
              ← Back
            </button>
            <h2>User Management</h2>
          </div>

          {detailLoading ? (
            <div className="ad-loading">Loading user…</div>
          ) : detailErr ? (
            <div className="ad-banner ad-banner--error">
              {detailErr}
            </div>
          ) : detail ? (
            <div className="ad-detail-grid">
              <div className="ad-card">
                <h3>Account</h3>
                <div className="ad-kv">
                  <span>Email</span>
                  <span className="ad-break">
                    {detail.auth.email || "—"}
                  </span>
                </div>
                <div className="ad-kv">
                  <span>UID</span>
                  <span className="ad-uid ad-break">
                    {detail.auth.uid}
                  </span>
                </div>
                <div className="ad-kv">
                  <span>Status</span>
                  <span>
                    {isSuspendedDetail(detail) ? (
                      <Badge kind="danger">Suspended</Badge>
                    ) : (
                      <Badge kind="ok">Active</Badge>
                    )}
                  </span>
                </div>
                <div className="ad-kv">
                  <span>Verified</span>
                  <span>
                    {detail.auth.emailVerified ? (
                      <Badge kind="ok">Yes</Badge>
                    ) : (
                      <Badge>No</Badge>
                    )}
                  </span>
                </div>

                <div className="ad-kv">
                  <span>MFA</span>
                  <span>
                    {detail.profile &&
                    detail.profile.mfa &&
                    detail.profile.mfa.enabled ? (
                      <Badge kind="ok">Enabled</Badge>
                    ) : (
                      <Badge>Not enabled</Badge>
                    )}
                  </span>
                </div>
                <div className="ad-row ad-gap ad-wrap">
                  {isSuspendedDetail(detail) ? (
                    // WHY: red "Un-suspend" while suspended for clear UX
                    <button
                      className="ad-btn ad-btn--danger"
                      onClick={() =>
                        unsuspend(detail.auth.uid)
                      }
                    >
                      Un-suspend
                    </button>
                  ) : (
                    <button
                      className="ad-btn"
                      onClick={() =>
                        suspend(detail.auth.uid)
                      }
                    >
                      Suspend
                    </button>
                  )}
                  <button
                    className="ad-btn"
                    onClick={() =>
                      setAdminFlag(
                        detail.auth.uid,
                        !!(detail.auth.customClaims &&
                        detail.auth.customClaims.admin)
                          ? false
                          : true
                      )
                    }
                  >
                    {detail.auth.customClaims &&
                    detail.auth.customClaims.admin
                      ? "Remove admin"
                      : "Make admin"}
                  </button>
                  <button
                    className="ad-btn"
                    onClick={() =>
                      detail.auth.email &&
                      sendReset(detail.auth.email)
                    }
                    disabled={!detail.auth.email}
                  >
                    Send reset
                  </button>
                  <button
                    className="ad-btn"
                    onClick={() =>
                      sendVerify(detail.auth.uid)
                    }
                  >
                    Send verification
                  </button>
                  <button
                    className="ad-btn"
                    onClick={() =>
                      markVerified(
                        detail.auth.uid,
                        !detail.auth.emailVerified
                      )
                    }
                  >
                    {detail.auth.emailVerified
                      ? "Mark unverified"
                      : "Mark verified"}
                  </button>
                  <button
                    className="ad-btn ad-btn--danger"
                    onClick={() =>
                      removeUser(detail.auth.uid)
                    }
                  >
                    Delete
                  </button>
                </div>
              </div>

              {/* Live usage widget (lazy) */}
              <Suspense
                fallback={
                  <div className="ad-card">
                    <div className="ad-loading">
                      Loading usage…
                    </div>
                  </div>
                }
              >
                <div
                  className="ad-card"
                  style={{ overflow: "hidden" }}
                >
                  <AdminUsageWidget uid={detail.auth.uid} />
                </div>
              </Suspense>

              <div className="ad-card">
                <h3>Plan</h3>

                <div className="ad-kv">
                  <span>Current plan</span>
                  <span className="ad-break">
                    {derivedPlan
                      ? derivedPlan.name ||
                        derivedPlan.key
                      : (detail.plan && detail.plan.id) ||
                        (detail.profile &&
                          (detail.profile.subscriptionPlan ||
                            detail.profile.plan ||
                            detail.profile.backendPlan)) ||
                        "—"}
                  </span>
                </div>

                <div className="ad-kv">
                  <span>Quota (storage)</span>
                  <span>
                    {derivedStorageLimit != null
                      ? fmtBytes(derivedStorageLimit)
                      : detail.plan &&
                        detail.plan.limits &&
                        detail.plan.limits.maxStorageBytes
                      ? fmtBytes(
                          detail.plan.limits.maxStorageBytes
                        )
                      : "—"}
                  </span>
                </div>

                <div className="ad-kv">
                  <span>Quota (items)</span>
                  <span>
                    {detail.plan &&
                    detail.plan.limits &&
                    typeof detail.plan.limits
                      .maxItems !== "undefined"
                      ? detail.plan.limits.maxItems
                      : "—"}
                  </span>
                </div>

                <div className="ad-row ad-gap ad-wrap">
                  <select
                    id="ad-planId"
                    className="ad-input"
                    defaultValue={
                      (detail.plan && detail.plan.id) ||
                      (detail.profile &&
                        (detail.profile.subscriptionPlan ||
                          detail.profile.plan ||
                          detail.profile.backendPlan)) ||
                      ""
                    }
                  >
                    <option value="">
                      Select plan…
                    </option>
                    <option value="basic">
                      Basic (1 GB)
                    </option>
                    <option value="standard">
                      Standard (3 GB)
                    </option>
                    <option value="advanced">
                      Advanced (10 GB)
                    </option>
                  </select>

                  <input
                    id="ad-maxStorageBytes"
                    className="ad-input"
                    type="number"
                    placeholder="maxStorageBytes"
                    defaultValue={
                      (detail.plan &&
                        detail.plan.limits &&
                        detail.plan.limits
                          .maxStorageBytes) ||
                      ""
                    }
                  />
                  <input
                    id="ad-maxItems"
                    className="ad-input"
                    type="number"
                    placeholder="maxItems"
                    defaultValue={
                      detail.plan &&
                      detail.plan.limits &&
                      typeof detail.plan.limits
                        .maxItems !== "undefined"
                        ? detail.plan.limits.maxItems
                        : ""
                    }
                  />
                  <button
                    className="ad-btn ad-btn--primary"
                    onClick={() =>
                      savePlan(detail.auth.uid)
                    }
                  >
                    Save plan
                  </button>
                </div>
              </div>

              <div className="ad-card">
                <h3>Billing & Subscription</h3>
                <div className="ad-kv">
                  <span>Status</span>
                  <span>
                    {(() => {
                      const s =
                        (detail.profile &&
                          (detail.profile
                            .subscriptionStatus ||
                            (detail.profile
                              .subscription &&
                              (detail.profile
                                .subscription.status ||
                                (detail.profile
                                  .subscription
                                  .active
                                  ? "active"
                                  : ""))))) ||
                        "";
                      const norm = String(s || "").toLowerCase();
                      const ok =
                        norm === "active" ||
                        norm === "trialing" ||
                        (detail.profile &&
                          detail.profile.subscription &&
                          detail.profile.subscription.active);
                      return ok ? (
                        <Badge kind="ok">
                          {s || "active"}
                        </Badge>
                      ) : s ? (
                        <Badge>{s}</Badge>
                      ) : (
                        "—"
                      );
                    })()}
                  </span>
                </div>
                <div className="ad-kv">
                  <span>Last paid</span>
                  <span>
                    {(() => {
                      const b =
                        detail.profile &&
                        detail.profile.billing;
                      if (!b) return "—";
                      const ts =
                        typeof b.lastPaidAtMs ===
                        "number"
                          ? b.lastPaidAtMs
                          : b.lastPaidAt &&
                            b.lastPaidAt.seconds
                          ? b.lastPaidAt.seconds *
                            1000
                          : null;
                      return ts ? tsToLocal(ts) : "—";
                    })()}
                  </span>
                </div>
                <div className="ad-kv">
                  <span>Amount</span>
                  <span>
                    {(() => {
                      const b =
                        detail.profile &&
                        detail.profile.billing;
                      if (!b || typeof b.amount !== "number")
                        return "—";
                      const amt = (b.amount / 100).toFixed(
                        2
                      );
                      const cur = (b.currency || "").toUpperCase();
                      return amt + " " + cur;
                    })()}
                  </span>
                </div>
                <div className="ad-kv">
                  <span>Receipt</span>
                  <span>
                    {detail.profile &&
                    detail.profile.billing &&
                    detail.profile.billing
                      .latestReceiptUrl ? (
                      <a
                        href={
                          detail.profile.billing
                            .latestReceiptUrl
                        }
                        target="_blank"
                        rel="noreferrer"
                      >
                        Open receipt
                      </a>
                    ) : (
                      "—"
                    )}
                  </span>
                </div>
                <div className="ad-row ad-gap ad-wrap">
                  <button
                    className="ad-btn"
                    onClick={() =>
                      openBillingPortal(detail.auth.uid)
                    }
                  >
                    Open Billing Portal
                  </button>
                  {detail.profile &&
                  detail.profile.stripeCustomerId ? (
                    <>
                      <a
                        className="ad-btn"
                        href={
                          "https://dashboard.stripe.com/customers/" +
                          detail.profile
                            .stripeCustomerId
                        }
                        target="_blank"
                        rel="noreferrer"
                      >
                        Open in Stripe (live)
                      </a>
                      <a
                        className="ad-btn"
                        href={
                          "https://dashboard.stripe.com/test/customers/" +
                          detail.profile
                            .stripeCustomerId
                        }
                        target="_blank"
                        rel="noreferrer"
                      >
                        Open in Stripe (test)
                      </a>
                    </>
                  ) : (
                    <span style={{ color: "#6b7280" }}>
                      No Stripe customer ID
                    </span>
                  )}
                </div>
              </div>

              <div className="ad-card">
                <h3>Account Managers</h3>
                {loadingManagers ? (
                  <div className="ad-loading">Loading…</div>
                ) : managers && managers.length > 0 ? (
                  <ul
                    className="ad-list"
                    style={{
                      listStyle: "none",
                      paddingLeft: 0,
                    }}
                  >
                    {managers.map((m) => (
                      <li
                        key={m.id}
                        style={{
                          padding: "8px 0",
                          borderBottom:
                            "1px dashed #eef",
                        }}
                      >
                        <div
                          className="ad-kv"
                          style={{
                            borderBottom: "0",
                            padding: "0",
                          }}
                        >
                          <span className="ad-break">
                            {m.name ||
                              m.recipient ||
                              "—"}{" "}
                            — {m.email || "—"}
                          </span>
                          <span>
                            {m.acceptedAt ? (
                              <span className="ad-badge ad-badge--ok">
                                Accepted
                              </span>
                            ) : (
                              <span className="ad-badge">
                                Pending
                              </span>
                            )}
                          </span>
                        </div>
                        <div
                          className="ad-row ad-gap ad-wrap"
                          style={{ marginTop: 6 }}
                        >
                          <button
                            className="ad-link"
                            onClick={() =>
                              adminResendManager(
                                detail.auth.uid,
                                m.id
                              )
                            }
                          >
                            Resend email
                          </button>
                          <button
                            className="ad-link"
                            onClick={() => openMgr(m)}
                          >
                            Edit
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="ad-empty">None</div>
                )}
              </div>
            </div>
          ) : null}
        </section>
      ) : null}

      {/* Admin Edit Manager Modal */}
      {mgrOpen ? (
        <div className="modal-bg" onClick={closeMgr}>
          <div
            className="modal-card"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 650 }}
          >
            <h3>
              {mgrEditingId
                ? "Edit Account Manager"
                : "Add Account Manager"}
            </h3>
            <div className="modal-body">
              <label className="ad-label">Name</label>
              <input
                className="ad-input"
                value={mgrForm.name}
                onChange={(e) =>
                  setMgrField("name", e.target.value)
                }
                placeholder="Full name"
              />

              <label className="ad-label">Email</label>
              <input
                className="ad-input"
                type="email"
                value={mgrForm.email}
                onChange={(e) =>
                  setMgrField("email", e.target.value)
                }
                placeholder="name@example.com"
              />

              <label className="ad-label">Delivery</label>
              <select
                className="ad-input"
                value={mgrForm.delivery}
                onChange={(e) =>
                  setMgrField("delivery", e.target.value)
                }
              >
                <option value="upon_death">
                  Upon death/admin approval
                </option>
                <option value="immediate">
                  Send immediately
                </option>
                <option value="scheduled">
                  Schedule a date/time
                </option>
              </select>

              {mgrForm.delivery === "scheduled" ? (
                <>
                  <label className="ad-label">
                    Deliver at
                  </label>
                  <input
                    className="ad-input"
                    type="datetime-local"
                    value={
                      mgrForm.deliverAt
                        ? new Date(
                            mgrForm.deliverAt
                          )
                            .toISOString()
                            .slice(0, 16)
                        : ""
                    }
                    onChange={(e) =>
                      setMgrField(
                        "deliverAt",
                        e.target.value
                      )
                    }
                  />
                </>
              ) : null}

              <label className="ad-label">
                Message to recipient
              </label>
              <textarea
                className="ad-input"
                rows={6}
                value={mgrForm.message}
                onChange={(e) =>
                  setMgrField("message", e.target.value)
                }
              />

              <label
                className="ad-label"
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                }}
              >
                <input
                  type="checkbox"
                  checked={mgrForm.includeSecret}
                  onChange={(e) =>
                    setMgrField(
                      "includeSecret",
                      e.target.checked
                    )
                  }
                />
                Include a private hint for the manager
              </label>
              {mgrForm.includeSecret ? (
                <>
                  <label className="ad-label">
                    Private hint
                  </label>
                  <textarea
                    className="ad-input"
                    rows={4}
                    value={mgrForm.secretNote}
                    onChange={(e) =>
                      setMgrField(
                        "secretNote",
                        e.target.value
                      )
                    }
                  />
                </>
              ) : null}

              {/* Page-level permissions */}
              <div style={{ marginTop: 14, padding: 12, border: "1px solid #eee", borderRadius: 10, background: "#fafafa" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                  <div style={{ fontWeight: 900, color: "#2a0516" }}>Page access</div>

                  <button
                    className="ad-btn ad-btn--primary"
                    onClick={adminSaveManagerPermissions}
                    disabled={!mgrEditingId || mgrPermBusy}
                  >
                    {mgrPermBusy ? "Saving…" : "Save permissions"}
                  </button>
                </div>

                <div className="ad-muted" style={{ fontSize: 12, marginTop: 6, lineHeight: 1.4 }}>
                  Account Managers are <b>read-only</b>. Defaults auto-apply when access is enabled. Grant <b>extra pages</b> below (Home is blocked by default).
                </div>

                <div style={{ marginTop: 10, border: "1px solid #f0f0f0", borderRadius: 10, padding: 10, background: "#fff" }}>
                  <div style={{ fontWeight: 800, marginBottom: 8 }}>Default access (always on)</div>
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {DEFAULT_MANAGER_MENU_KEYS.map((k) => {
                      const p = PAGE_ACCESS.find((x) => x.key === k);
                      return <li key={k}>{p?.label || k}</li>;
                    })}
                  </ul>
                </div>

                <div style={{ marginTop: 10, border: "1px solid #f0f0f0", borderRadius: 10, padding: 10, background: "#fff" }}>
                  <div style={{ fontWeight: 800, marginBottom: 8 }}>Optional extra pages</div>
                  <div style={{ display: "grid", gap: 10 }}>
                    {EXTRA_MANAGER_PAGES.map((p) => (
                      <label key={p.key} style={{ display: "grid", gridTemplateColumns: "22px 1fr", gap: 10, alignItems: "start" }}>
                        <input
                          type="checkbox"
                          style={{ marginTop: 2 }}
                          checked={mgrPageAccess[p.key] === true}
                          onChange={(e) => adminTogglePage(p.key, e.target.checked)}
                          disabled={mgrPermBusy}
                        />
                        <div>
                          <div style={{ fontWeight: 800 }}>{p.label}</div>
                          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>{p.description || ""}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

            </div>
            <div
              className="form-actions"
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              <button
                className="ad-btn ad-btn--primary"
                onClick={adminSaveManager}
              >
                {mgrEditingId ? "Update" : "Save"}
              </button>
              {mgrEditingId ? (
                <button
                  className="ad-btn ad-btn--danger"
                  onClick={adminDeleteManager}
                >
                  Delete
                </button>
              ) : null}
              <button
                className="ad-btn"
                onClick={closeMgr}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <AdStyles />
    </div>
  );
}

export default function AdminDashboard() {
  return (
    <AdminErrorBoundary>
      <AdminDashboardInner />
    </AdminErrorBoundary>
  );
}

function AdStyles() {
  return (
    <style>{`
      .ad *, .ad *::before, .ad *::after { box-sizing: border-box; }
      .ad-wrap{max-width:1200px;margin:24px auto;padding:0 16px;width:100%}
      .ad-header{display:flex;justify-content:space-between;align-items:center;gap:12px;margin:8px 0 16px}
      .ad-tabs{display:flex;gap:8px;flex-wrap:wrap}
      .ad-tab{padding:10px 14px;border:1px solid #d6d6e7;border-radius:10px;background:#fff;cursor:pointer;line-height:1;text-decoration:none;color:inherit}
      .ad-tab.is-active{background:#111827;color:#fff;border-color:#111827}

      .ad-input{height:38px;padding:0 12px;border:1px solid #d6d6e7;border-radius:10px}
      .ad-label{font-weight:600;color:#374151;margin-top:8px;margin-bottom:4px;display:block}
      .ad-btn{height:38px;padding:0 14px;border-radius:10px;border:1px solid #cfcfe6;background:#fff;cursor:pointer;display:inline-flex;align-items:center;justify-content:center}
      .ad-btn--primary{background:#4f46e5;color:#fff;border-color:#4f46e5}
      .ad-btn--danger{background:#dc2626;color:#fff;border-color:#dc2626}
      .ad-btn:disabled{opacity:.6;cursor:not-allowed}

      .ad-banner{padding:10px 12px;border-radius:10px;margin:8px 0;border:1px solid}
      .ad-banner--error{background:#fee2e2;color:#7a0b0b;border-color:#fecaca}

      .ad-table-wrap{width:100%;overflow:auto}
      .ad-table{width:100%;border-collapse:separate;border-spacing:0;border:1px solid #e5e7eb;border-radius:12px;overflow:hidden}
      .ad-table thead th{background:#f9fafb;text-align:left;font-weight:600;padding:12px;border-bottom:1px solid #e5e7eb;white-space:nowrap}
      .ad-table td{padding:12px;border-top:1px solid #f3f4f6;vertical-align:middle}
      .ad-actions{text-align:right;white-space:nowrap}
      .ad-loading,.ad-empty{padding:14px;text-align:center}
      .ad-break{word-break:break-word;overflow-wrap:anywhere}
      .ad-muted{color:#6b7280}

      @media (max-width: 720px){
        .ad-table thead{display:none}
        .ad-table tr{display:block;border-bottom:1px solid #f3f4f6}
        .ad-table td{display:flex;justify-content:space-between;gap:12px}
        .ad-table td::before{content:attr(data-label);font-weight:600;color:#6b7280}
        .ad-actions{justify-content:flex-end}
      }

      .ad-pager{display:flex;gap:8px;margin-top:10px}
      .ad-badge{display:inline-flex;align-items:center;gap:6px;padding:2px 8px;border-radius:999px;border:1px solid #e5e7eb;font-size:12px;line-height:18px}
      .ad-badge--ok{background:#ecfdf5;border-color:#a7f3d0;color:#065f46}
      .ad-badge--danger{background:#fef2f2;border-color:#fecaca;color:#7f1d1d}

      .ad-detail .ad-detail-header{display:flex;align-items:center;gap:12px;margin-bottom:10px}
      .ad-detail-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(280px,1fr));gap:16px}
      @media (min-width: 1280px){ .ad-detail-grid{grid-template-columns:repeat(3,minmax(0,1fr));} }
      @media (max-width: 480px){ .ad-detail-grid{gap:12px} }

      .ad-card{background:#fff;border:1px solid #e5e7eb;border-radius:12px;padding:12px;min-width:0}
      .ad-card h3{margin:0 0 8px}
      .ad-kv{display:flex;justify-content:space-between;gap:16px;padding:6px 0;border-bottom:1px dashed #eef;align-items:center;flex-wrap:wrap}
      .ad-kv span:first-child{color:#6b7280}
      .ad-uid{font-family:ui-monospace,Menlo,Consolas,monospace;font-size:12px;opacity:.9;word-break:break-all}

      .ad-row{display:flex;align-items:center}
      .ad-gap{gap:8px}
      .ad-wrap{flex-wrap:wrap}

      .ad-list{margin:0}
      .ad-list li{padding:4px 0}
      .ad-link{background:none;border:none;color:#2563eb;cursor:pointer;padding:0}

      /* Modal */
      .modal-bg{position:fixed;inset:0;background:rgba(0,0,0,.26);display:flex;align-items:center;justify-content:center;padding:16px;z-index:1000}
      .modal-card{background:#fff;border-radius:12px;border:1px solid #e5e7eb;padding:12px;width:100%}
    `}</style>
  );
}